/**
 * Activity Tracking Service
 *
 * Reports which page each open browser tab is viewing — and whether that tab is
 * the user's focused (foreground) tab — by joining a Laravel Reverb *presence*
 * channel named:
 *
 *     activity.{status}.page.{base64url(pathname)}
 *
 * where {status} is `active` (this tab is focused & visible) or `idle`. A tab is
 * a member of exactly one such channel at a time and re-joins whenever:
 *   - the page changes (Inertia navigation), or
 *   - the tab gains/loses focus or visibility.
 *
 * So the same connection moves between the `active` and `idle` channel for its
 * page as the user tabs in and out. The backend reads this presence state via
 * `php artisan activity:current` (App\Services\Activity\ActivityTrackingService).
 * Authorization lives in routes/channels.php. The page is base64url encoded
 * because raw paths contain "/", which is not a legal channel-name character.
 */
import { router } from '@inertiajs/vue3';

interface EchoLike {
    join(channel: string): unknown;
    leave(channel: string): void;
}

function getEcho(): EchoLike | undefined {
    return (window as unknown as { Echo?: EchoLike }).Echo;
}

/** base64url encode a string (URL/channel-safe, no padding). */
function encodePage(path: string): string {
    const base64 = btoa(unescape(encodeURIComponent(path)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** This tab is "active" when it is both visible and holds OS focus. */
function isActive(): boolean {
    return !document.hidden && document.hasFocus();
}

function channelFor(path: string): string {
    const status = isActive() ? 'active' : 'idle';
    return `activity.${status}.page.${encodePage(path)}`;
}

let currentPath = '/';
let currentChannel: string | null = null;

/** Join the channel that matches the current page + focus, leaving any previous one. */
function sync(): void {
    const echo = getEcho();
    if (!echo) {
        return;
    }

    const channel = channelFor(currentPath);
    if (channel === currentChannel) {
        return;
    }

    if (currentChannel) {
        echo.leave(currentChannel);
    }

    echo.join(channel);
    currentChannel = channel;
}

/**
 * Begin tracking page activity for this tab. Safe to call once during app
 * bootstrap; a no-op if Echo/Reverb is not configured.
 */
export function startActivityTracking(): void {
    if (!getEcho()) {
        return;
    }

    currentPath = window.location.pathname;
    sync();

    // Page changed (SPA navigation, no full reload).
    router.on('navigate', () => {
        currentPath = window.location.pathname;
        sync();
    });

    // Focus / visibility changed — re-evaluate active vs idle for the same page.
    window.addEventListener('focus', sync);
    window.addEventListener('blur', sync);
    document.addEventListener('visibilitychange', sync);
}
